import { FC, useEffect, useRef } from 'react';

import professionScreenShown, {
    Region,
} from '@hh.ru/analytics-js-events/build/career_platform/profession/profession_screen_shown';
import {
    Divider,
    GridColumn,
    GridLayout,
    GridRow,
    Layer,
    Text,
    useBreakpoint,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';

import Footer from 'src/components/Footer';
import Header from 'src/components/Header';
import PageWrapper from 'src/components/PageWrapper';
import ThemeContainer, { ThemeContainerBackground, ThemeContainerVariant } from 'src/components/ThemeContainer';
import TitleSecondary, { TitleSecondarySize } from 'src/components/TitleSecondary';
import { AREA_MOSCOW, AREA_RUSSIA, AREA_SBP } from 'src/fixtures/areas';
import useFeatureEnabled from 'src/hooks/useFeatureEnabled';
import useUserArea from 'src/hooks/useUserArea';
import { AreaId } from 'src/types/area';
import GeoLocation from 'src/widgets/GeoLocation';

import Breadcrumbs from 'src/pages/Profession/components/Breadcrumbs';
import CardMatchSkills from 'src/pages/Profession/components/CardMatchSkills';
import CardMatchSkillsLogin from 'src/pages/Profession/components/CardMatchSkillsLogin';
import CurrentGradeDescription from 'src/pages/Profession/components/CurrentGradeDescription';
import FAQProfession from 'src/pages/Profession/components/FAQProfession';
import MainAnnotation from 'src/pages/Profession/components/MainAnnotation';
import MainBackgroundIcon from 'src/pages/Profession/components/MainBackgroundIcon';
import NavGrades from 'src/pages/Profession/components/NavGrades';
import NavRegions from 'src/pages/Profession/components/NavRegions';
import NavSections from 'src/pages/Profession/components/NavSections';
import SectionsContent from 'src/pages/Profession/components/SectionsContent';

import styles from './styles.less';

const Features = {
    isEnabledGeoButton: 'enabled_geo_location_button',
};

const PageLayout: FC = () => {
    const { profession, professionId, currentSection, currentGrade, currentRegion, matchIndicator } =
        useSelectorNonNullable((state) => state.pageProfession);
    const { area } = useUserArea();
    const { isMobile } = useBreakpoint();
    const isEnabledGeoButton = useFeatureEnabled(Features.isEnabledGeoButton);

    const sectionsNavContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef<HTMLDivElement>(null);

    const handleChangeSection = () => {
        const isScrolled = Boolean(
            sectionsNavContainerRef.current && sectionsNavContainerRef.current.getClientRects()[0].top <= 0
        );

        if (isScrolled) {
            scrollPositionRef.current?.scrollIntoView();
        }
    };

    useEffect(() => {
        const analyticsRegion: Record<AreaId, Region> = {
            [AREA_MOSCOW.id]: 'Moscow',
            [AREA_SBP.id]: 'SPB',
            [AREA_RUSSIA.id]: 'Russia',
        };

        professionScreenShown({
            professionId,
            levelId: currentGrade?.id || '',
            screenType: currentSection,
            areaId: area.id,
            areaName: area.text,
            region: analyticsRegion[currentRegion] || 'Regions',
        });
    }, [currentSection, professionId, currentGrade, area, currentRegion]);

    return (
        <PageWrapper>
            <div className={styles.wrapper}>
                <ThemeContainer variant={ThemeContainerVariant.Night} background={ThemeContainerBackground.Secondary}>
                    <div className={styles.container}>
                        <Header />
                        <GridLayout>
                            <div className={styles.breadcrumbs}>
                                <Breadcrumbs />
                            </div>
                            <GeoLocation type="mobile-banner" />
                            <div className={styles.mainContent}>
                                <GridRow>
                                    <GridColumn xs={4} s={8} m={8} xl={7}>
                                        <main>
                                            <TitleSecondary
                                                Element="h1"
                                                size={isMobile ? TitleSecondarySize.Size2 : TitleSecondarySize.Size1}
                                                data-qa="page-title"
                                            >
                                                {profession.name}
                                            </TitleSecondary>
                                            <Text typography="paragraph-2-regular" Element="p">
                                                {profession.description}
                                            </Text>
                                            <VSpacing default={32} />
                                            <NavGrades />
                                            <VSpacing default={16} />
                                            <CurrentGradeDescription initialShowCount={isMobile ? 2 : 3} />
                                        </main>
                                    </GridColumn>
                                    <GridColumn xs={0} s={0} m={0} xl={1} />
                                    <GridColumn xs={4} s={8} m={4} xl={4}>
                                        <div className={styles.cardMatchSkillsContainer}>
                                            {matchIndicator ? (
                                                <CardMatchSkills values={matchIndicator} professionId={professionId} />
                                            ) : (
                                                <CardMatchSkillsLogin professionId={professionId} />
                                            )}
                                        </div>
                                    </GridColumn>
                                </GridRow>
                                {isEnabledGeoButton && (
                                    <div className={styles.regionContainer}>
                                        <NavRegions />
                                    </div>
                                )}
                                <div className={styles.mainAnnotationContainer}>
                                    <MainAnnotation />
                                </div>
                            </div>
                        </GridLayout>
                        <div className={styles.mainBackgroundIcon}>
                            <MainBackgroundIcon />
                        </div>
                    </div>
                </ThemeContainer>
            </div>
            <div ref={scrollPositionRef} />
            <Layer layer={'alpha'}>
                <div className={styles.sectionsNavContainer} ref={sectionsNavContainerRef}>
                    <GridLayout>
                        <NavSections onChange={handleChangeSection} />
                    </GridLayout>
                    <Divider />
                </div>
            </Layer>
            <Layer layer={'content'}>
                <div className={styles.sectionsContent}>
                    <GridLayout>
                        <VSpacingContainer default={40}>
                            <SectionsContent />
                            <FAQProfession />
                        </VSpacingContainer>
                    </GridLayout>
                </div>
            </Layer>
            <Footer />
        </PageWrapper>
    );
};

export default PageLayout;
